<template>
  <div class="phone-input" :class="$attrs.class">
    <span class="phone-input__prefix">+7</span>
    <input
      ref="inputRef"
      class="phone-input__field"
      :value="maskedValue"
      type="tel"
      inputmode="numeric"
      autocomplete="tel"
      :placeholder="placeholder"
      :disabled="disabled"
      maxlength="15"
      @keydown="onKeydown"
      @beforeinput="onBeforeInput"
      @input="onInput"
      @paste="onPaste"
    />
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import {
  caretAfterDigitIndex,
  countDigitsBefore,
  digitsOnly,
  formatNationalMasked,
  normalizeRuPhoneE164,
  parseRuPhoneDigits,
} from '@/utils/ruPhoneMask'

defineOptions({ inheritAttrs: false })

const props = defineProps({
  modelValue: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  placeholder: { type: String, default: '(___) ___-__-__' },
})

const emit = defineEmits(['update:modelValue'])

const inputRef = ref(null)
const national = ref('')
const maskedValue = ref('')

/** Длина полной маски (999) 999-99-99 */
const MASK_MAX_LENGTH = 15

function syncFromModel() {
  national.value = parseRuPhoneDigits(props.modelValue)
  maskedValue.value = formatNationalMasked(national.value)
}

watch(() => props.modelValue, syncFromModel, { immediate: true })

function emitModel() {
  if (national.value.length === 10) {
    emit('update:modelValue', normalizeRuPhoneE164(national.value))
  } else {
    emit('update:modelValue', '')
  }
}

function selectionDigitRange(el) {
  const raw = el.value ?? ''
  const start = el.selectionStart ?? raw.length
  const end = el.selectionEnd ?? start
  return {
    start: countDigitsBefore(raw, start),
    end: countDigitsBefore(raw, end),
    hasSelection: end > start,
  }
}

function applyNationalDigits(digits, caretDigitIndex) {
  national.value = digits.slice(0, 10)
  maskedValue.value = formatNationalMasked(national.value)
  emitModel()

  const pos = caretAfterDigitIndex(
    maskedValue.value,
    Math.min(caretDigitIndex, national.value.length),
  )
  const el = inputRef.value
  if (!el) return
  el.value = maskedValue.value
  try {
    el.setSelectionRange(pos, pos)
  } catch {
    /* ignore */
  }
}

function onInput(event) {
  const el = event.target
  const raw = String(el.value ?? '')
  const sel = el.selectionStart ?? raw.length
  const digitIndex = countDigitsBefore(raw, sel)
  const digits = digitsOnly(raw).slice(0, 10)
  applyNationalDigits(digits, digitIndex)
}

function onPaste(event) {
  event.preventDefault()
  const text = event.clipboardData?.getData('text') ?? ''
  const pasted = parseRuPhoneDigits(text)
  const el = inputRef.value
  if (!el) return
  const { start, end } = selectionDigitRange(el)
  const merged = (national.value.slice(0, start) + pasted + national.value.slice(end)).slice(0, 10)
  applyNationalDigits(merged, Math.min(start + pasted.length, 10))
}

const CONTROL_KEYS = new Set([
  'Backspace',
  'Delete',
  'Tab',
  'Escape',
  'Enter',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Home',
  'End',
])

function onKeydown(event) {
  if (props.disabled) return
  if (event.ctrlKey || event.metaKey || event.altKey) return
  if (CONTROL_KEYS.has(event.key)) return

  if (event.key.length === 1 && !/\d/.test(event.key)) {
    event.preventDefault()
    return
  }

  if (!/\d/.test(event.key)) return

  const el = inputRef.value
  if (!el) return
  const { start, end, hasSelection } = selectionDigitRange(el)
  if (national.value.length >= 10 && !hasSelection) {
    event.preventDefault()
    return
  }
  if (hasSelection) return
  if (start >= 10) {
    event.preventDefault()
  }
}

function onBeforeInput(event) {
  if (props.disabled) return

  const type = event.inputType || ''
  if (type.startsWith('delete') || type === 'insertLineBreak') return

  const data = event.data
  if (data != null && [...data].some((ch) => !/\d/.test(ch))) {
    event.preventDefault()
    return
  }

  if (!data || !/\d/.test(data)) return

  const el = inputRef.value
  if (!el) return
  const { start, end, hasSelection } = selectionDigitRange(el)
  const selectedDigits = end - start
  const room = 10 - (national.value.length - selectedDigits)
  if (room <= 0) {
    event.preventDefault()
  }
}

</script>

<style scoped>
.phone-input {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  box-sizing: border-box;
}

.phone-input__prefix {
  flex-shrink: 0;
  user-select: none;
  color: inherit;
  font-weight: 500;
  line-height: 1.2;
}

.phone-input__field {
  flex: 1;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  color: inherit;
  font: inherit;
  line-height: inherit;
  outline: none;
  box-shadow: none;
}

.phone-input__field:disabled {
  cursor: not-allowed;
}

.phone-input__field::placeholder {
  color: #86868b;
}
</style>
